import React from 'react';
import { Section } from '../../types/Section';
import { SectionContainer } from './SectionContainer';
import {
  FileText,
  Download,
  Image,
  Film,
  Music,
  Archive,
  File,
  FileSpreadsheet,
  FileCode,
  Presentation
} from 'lucide-react';

interface Attachment {
  id: string;
  name?: string;
  description?: string;
  position?: number;
  fileUrl?: string;
  fileFileName?: string;
  fileContentType?: string;
  fileFileSize?: number;
  fileUpdatedAt?: string;
  formattedFileSize?: string;
  fileType?: string;
  isImage?: boolean;
  imageThumbUrl?: string;
  imageMediumUrl?: string;
  imageLargeUrl?: string;
  showAt?: string;
  isVisible?: boolean;
  downloadsCount?: number;
  uniqueDownloadsCount?: number;
}

interface FilesSectionProps {
    section: Section & {
        attachments?: Attachment[];
    };
}

export const FilesSection: React.FC<FilesSectionProps> = ({ section }) => {
    const formatFileSize = (bytes?: number) => {
        if (!bytes) return 'Unknown size';

        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        if (bytes === 0) return '0 Bytes';

        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const getFileIcon = (contentType?: string, fileName?: string) => {
        // Check file extension first for more accurate detection
        if (fileName) {
            const extension = fileName.split('.').pop()?.toLowerCase();

            // PDF files
            if (extension === 'pdf') return FileText;

            // Images
            if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(extension || '')) return Image;

            // Word documents
            if (['doc', 'docx', 'odt', 'rtf'].includes(extension || '')) return FileText;

            // Excel/Spreadsheets
            if (['xls', 'xlsx', 'csv', 'ods'].includes(extension || '')) return FileSpreadsheet;

            // PowerPoint/Presentations
            if (['ppt', 'pptx', 'odp'].includes(extension || '')) return Presentation;

            // Video files
            if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'].includes(extension || '')) return Film;

            // Audio files
            if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma'].includes(extension || '')) return Music;

            // Archives
            if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'].includes(extension || '')) return Archive;

            // Code/Text files
            if (['txt', 'md', 'json', 'xml', 'yaml', 'yml'].includes(extension || '')) return FileCode;
        }

        // Fall back to content type checking
        if (!contentType) return File;

        const type = contentType.toLowerCase();

        // Check content types (for cases where extension doesn't match)
        if (type.includes('pdf')) return FileText;
        if (type.includes('image')) return Image;
        if (type.includes('word') || type.includes('document')) return FileText;
        if (type.includes('excel') || type.includes('spreadsheet')) return FileSpreadsheet;
        if (type.includes('powerpoint') || type.includes('presentation')) return Presentation;
        if (type.includes('video')) return Film;
        if (type.includes('audio')) return Music;
        if (type.includes('zip') || type.includes('compressed') || type.includes('archive')) return Archive;
        if (type.includes('text')) return FileCode;

        // Default
        return File;
    };

    const files = section.attachments || [];

    if (files.length === 0) {
        return (
            <SectionContainer section={section}>
                <h2 className="text-2xl font-bold mb-4">{section.title}</h2>
                <p className="text-gray-500">No files available for download.</p>
            </SectionContainer>
        );
    }

    return (
        <SectionContainer section={section}>
            <h2 className="text-2xl font-bold mb-6">{section.title}</h2>
            <div className="flex flex-wrap gap-4">
                {files.map((file) => {
                    // Use name first, then fall back to fileFileName from Shrine
                    const fileName = file.name || file.fileFileName || 'Unnamed File';
                    const Icon = getFileIcon(file.fileContentType, fileName);
                    // Use the pre-formatted file size from the serializer
                    const fileSize = file.formattedFileSize || formatFileSize(file.fileFileSize);

                    return (
                        <a
                            key={file.id}
                            href={file.fileUrl || '#'}
                            download={fileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 hover:shadow-md transition-all cursor-pointer min-w-[200px] max-w-full"
                            style={{ flex: '1 1 auto' }}
                            title={`Download ${fileName}`}
                        >
                            {/* File icon */}
                            <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                                <Icon size={24} className="text-blue-600" />
                            </div>

                            {/* File info */}
                            <div className="flex-1 min-w-0 pr-8">
                                <p className="font-medium text-gray-900 break-words line-clamp-2" title={fileName}>
                                    {fileName}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">{fileSize}</p>
                                {file.description && (
                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{file.description}</p>
                                )}
                            </div>

                            {/* Download icon - positioned absolutely */}
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 group-hover:opacity-100 transition-opacity">
                                <Download size={18} className="text-gray-600 group-hover:text-blue-600" />
                            </div>
                        </a>
                    );
                })}
            </div>
        </SectionContainer>
    );
};
